// Les formules du prix, une seule fois pour les deux écrans qui les
// affichent : le bloc de prix des formulaires (prix_detail_controller.js,
// Stimulus) et l'éditeur de cours (PrixBloc.svelte). Ce sont celles
// d'ApercuPrix, côté serveur, avec le même arrondi au millime à chaque étape :
// un écart entre les deux serait un écart entre l'aperçu et la reddition.
//
// `t` porte les taux, jamais en dur ici : { taux, tva_beb, cession, part,
// frais_carte } (ApercuPrix.taux, en pourcents), et `retenue_honoraires`
// pour une consultation seulement (prix_detail_attrs honoraires: true).
//
// ⛔ Le Prestataire ne fixe pas le prix public (CGU 9 bis.1) : il déclare ce
// qu'il veut toucher, et prixPublicPourNet en déduit le dinar entier que BEB
// affiche. Le prix enregistré est TOUJOURS ce prix public.

export const millime = (n) => Math.round(n * 1000) / 1000
export const arrondi = (n) => millime(n).toFixed(3)

export function nombre(brut) {
  const n = parseFloat(String(brut ?? "").replace(",", "."))
  return Number.isFinite(n) ? n : null
}

// Net par carte : hors taxe après la TVA de BEB, Prix de Cession, moins les
// frais carte sur le prix public. C'est le plus bas des deux canaux.
export function nette(ttc, t) {
  const ht    = millime(ttc / (1 + t.tva_beb / 100))
  const brute = millime(ht * t.cession / 100)
  const frais = millime(ttc * t.frais_carte / 100)
  return millime(brute - frais)
}

// Les mêmes clés qu'ApercuPrix#cellules.
export function cellules(ttc, t) {
  const htCarte = millime(ttc / (1 + t.tva_beb / 100))
  const cession = millime(htCarte * t.cession / 100)
  const frais   = millime(ttc * t.frais_carte / 100)
  const htOutil = millime(ttc / (1 + t.taux / 100))
  const part    = millime(htOutil * t.part / 100)
  return {
    ttc_carte:     ttc,
    tva_carte:     millime(ttc - htCarte),
    ht_carte:      htCarte,
    marge_carte:   millime(htCarte - cession),
    cession_carte: cession,
    frais_carte:   frais,
    net_carte:     millime(cession - frais),
    // ApercuPrix#retenue_carte et #verse_carte : la retenue d'une
    // consultation, sur la ligne nette des frais carte.
    retenue_carte: millime((cession - frais) * (t.retenue_honoraires || 0) / 100),
    verse_carte:   millime(cession - frais - millime((cession - frais) * (t.retenue_honoraires || 0) / 100)),
    ttc_outil:     ttc,
    tva_outil:     millime(ttc - htOutil),
    ht_outil:      htOutil,
    part_outil:    part,
    reste_outil:   millime(ttc - part)
  }
}

// Le plus petit prix au millime dont le net par carte atteint `net` (même
// boucle qu'ApercuPrix.ttc_pour_net_carte), puis le dinar supérieur
// (ApercuPrix.prix_public_pour_net). null quand il n'y a pas de net.
export function prixPublicPourNet(net, t) {
  if (!(net > 0)) return null
  const facteur = t.cession / 100 / (1 + t.tva_beb / 100) - t.frais_carte / 100
  let ttc = Math.ceil(net / facteur * 1000) / 1000
  while (nette(millime(ttc - 0.001), t) >= net) ttc = millime(ttc - 0.001)
  while (nette(ttc, t) < net) ttc = millime(ttc + 0.001)
  return Math.ceil(ttc)
}

const fmtTnd = new Intl.NumberFormat("fr-TN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
const fmtPct = new Intl.NumberFormat("fr-TN", { maximumFractionDigits: 2 })
export const formatTnd = (n) => fmtTnd.format(n)
export const formatPct = (n) => `${fmtPct.format(n)} %`
